/**
 * FilterFieldSet
 * @module components/FilterFieldSet
 */

import { useContext, useState } from 'react';
import { LocalizationContext } from 'context/localization.context';
import { Fieldset } from './FilterFieldSet.styles';
import { FacetItemModel } from 'types/filter-types';
import FilterOption from './FilterOption';
import FilterByRegion from './FilterByRegion';
import { FilterFacets } from 'types/common';
import FilterByLocationType from './FilterByLocationType';
import { useMedia } from 'hooks';
import { FiChevronDown } from 'react-icons/fi';
import { useSiteType } from 'context/siteType.context';
import clsx from 'clsx';
import Icon from 'components/ui/Icon';

interface FilterFieldSetModel {
	name: string;
	items: FacetItemModel[];
	facetId: string;
	className?: string;
	onChange: (facetId: string, id: string, active: boolean) => void;
	defaultNumberToShow?: number;
	alternateDisplay?: boolean;
	showFieldset?: boolean;
	isNewContactPage?: boolean;
	isOrderHistory?: boolean;
}

const FilterFieldSet = ({
	alternateDisplay,
	name,
	items,
	facetId,
	className,
	onChange,
	defaultNumberToShow,
	isNewContactPage = false,
	isOrderHistory = false,
}: FilterFieldSetModel) => {
	const { t }: any = useContext(LocalizationContext);
	const isMobile = useMedia('(max-width: 1300px)');
	const [expanded, setExpanded] = useState(false);
	const isLindabGroup = useSiteType() === 'LindabGroup';
	const toggleShowMore = () => {
		setExpanded(!expanded);
	};

	const numberToShow = defaultNumberToShow || 5;

	const handleChange = (event: any) => {
		onChange(facetId, event.target.value, true);
	};

	// Calculate if there are more items to show to show dropdown if it does not fit in the container
	const names: string[] = items.map((item) => item.name);
	const totalLength: number = names.reduce(
		(acc, name) =>
			acc + name.length + (FilterFacets.LOCATION_TYPE === facetId ? 0 : 3),
		0
	);

	return (
		<>
			{isNewContactPage ? (
				<>
					{(isMobile || totalLength > 114) &&
					(FilterFacets.REGION === facetId ||
						FilterFacets.LOCATION_TYPE === facetId) ? (
						<div className="w-full relative inline-block text-left">
							<select
								className="focus:outline-none new-location-row-details new-location-row-heading my-2.5 appearance-none"
								name={facetId}
								id={facetId}
								onChange={handleChange}
							>
								{items.map((item, index) => (
									<option value={item.id} key={index + item.id + item.name}>
										{item.name}
									</option>
								))}
							</select>
							<div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
								<FiChevronDown size={25} />
							</div>
						</div>
					) : (
						<>
							{FilterFacets.REGION === facetId && (
								<ul
									id={facetId}
									className={clsx(
										'flex my-5 border-b border-greyDarker',
										isLindabGroup ? 'h-49px' : 'h-auto'
									)}
								>
									{items.map((item) => (
										<FilterByRegion
											key={`${name}-${item.id}`}
											item={item}
											facetId={facetId}
											onChange={onChange}
											className="mr-4"
										/>
									))}
								</ul>
							)}
							{FilterFacets.LOCATION_TYPE === facetId && (
								<ul
									id={facetId}
									className="flex mb-12 flex-wrap gap-y-4 border-b border-greyDarker pb-4"
								>
									{items.map((item) => (
										<FilterByLocationType
											key={`${name}-${item.id}`}
											item={item}
											facetId={facetId}
											onChange={onChange}
										/>
									))}
								</ul>
							)}
						</>
					)}
				</>
			) : (
				<Fieldset className={className} aria-live="polite">
					{name && (
						//@ts-ignore
						<legend
							className={clsx(
								'm-0',
								isLindabGroup
									? 'text-lgDarkBlue text-p'
									: 'text-black font-bold text-h4 font-standard'
							)}
						>
							{name}
						</legend>
					)}
					{items && items.length < numberToShow ? (
						items.map((item) => (
							<FilterOption
								isOrderHistory={isOrderHistory}
								alternateDisplay={alternateDisplay}
								item={item}
								facetId={facetId}
								onChange={onChange}
								key={`${name}-${item.id}`}
							/>
						))
					) : (
						<>
							{items.map((item, i) => {
								if (expanded || i < numberToShow - 1) {
									return (
										<FilterOption
											alternateDisplay={alternateDisplay}
											item={item}
											facetId={facetId}
											onChange={onChange}
											key={`${name}-${item.id}`}
										/>
									);
								}
								return false;
							})}
							<button
								className="inline-flex items-center gap-1.5 font-standard text-p font-normal border-0 p-0 bg-transparent text-blue cursor-pointer mt-0 mb-3 hover:underline md:mb-0"
								onClick={(e: any) => {
									e.preventDefault();
									toggleShowMore();
								}}
								aria-expanded={expanded}
							>
								<Icon size={0.75} icon={expanded ? 'minus' : 'plus'} />
								{expanded
									? t('shared/filter/lessoptions')
									: t('shared/filter/moreoptions')}
							</button>
						</>
					)}
				</Fieldset>
			)}
		</>
	);
};

export default FilterFieldSet;
